import { useEffect } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Persona, ToneType, PedagogyStyleType } from '../types'
import { personaSchema, type PersonaFormData } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

interface PersonaFormProps {
  persona?: Persona
  isPending: boolean
  error?: string | null
  onSubmit: (data: PersonaFormData) => void
  onCancel: () => void
}

const TONES: { value: ToneType; icon: string }[] = [
  { value: 'encouraging', icon: '🌟' },
  { value: 'strict', icon: '📐' },
  { value: 'playful', icon: '🎮' },
  { value: 'neutral', icon: '⚖️' },
]

const STYLES: { value: PedagogyStyleType; icon: string }[] = [
  { value: 'socratic', icon: '💭' },
  { value: 'direct', icon: '🎯' },
  { value: 'collaborative', icon: '🤝' },
  { value: 'adaptive', icon: '🔄' },
]

export function PersonaForm({ persona, isPending, error, onSubmit, onCancel }: PersonaFormProps) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<PersonaFormData>({
    resolver: zodResolver(personaSchema),
    defaultValues: persona
      ? {
        name: persona.name,
        description: persona.description ?? '',
        tone: persona.tone,
        pedagogy_style: persona.pedagogy_style,
      }
      : { name: '', tone: 'encouraging', pedagogy_style: 'direct', description: '' },
  })

  useEffect(() => {
    if (persona) {
      reset({
        name: persona.name,
        description: persona.description ?? '',
        tone: persona.tone,
        pedagogy_style: persona.pedagogy_style,
      })
    }
  }, [persona, reset])

  const watchedName = useWatch({ control, name: 'name' })
  const watchedTone = useWatch({ control, name: 'tone' })
  const watchedStyle = useWatch({ control, name: 'pedagogy_style' })
  const selectedTone = TONES.find((tone) => tone.value === watchedTone)?.value
  const selectedStyle = STYLES.find((style) => style.value === watchedStyle)?.value

  return (
    <div className="flex gap-6">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-4" noValidate>
        {error && <Alert variant="error">{error}</Alert>}

        <Input
          label={t('personas.form.name')}
          placeholder={t('personas.form.name_placeholder')}
          error={errors.name?.message && t(errors.name.message)}
          {...register('name')}
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-neutral-800">{t('personas.form.tone')}</span>
          <Controller
            name="tone"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                {TONES.map((tone) => (
                  <button
                    key={tone.value}
                    type="button"
                    aria-pressed={field.value === tone.value}
                    onClick={() => field.onChange(tone.value)}
                    className={[
                      'flex min-h-[44px] items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary',
                      field.value === tone.value
                        ? 'border-mindeia-primary bg-mindeia-primary-light text-mindeia-primary'
                        : 'border-surface-200 bg-white text-neutral-700 hover:border-mindeia-primary',
                    ].join(' ')}
                  >
                    <span aria-hidden="true">{tone.icon}</span>
                    {t(`teaching.tones.${tone.value}`)}
                  </button>
                ))}
              </div>
            )}
          />
          {errors.tone && <p role="alert" className="text-xs text-mindeia-error">{t(errors.tone.message ?? '')}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-neutral-800">{t('personas.form.style')}</span>
          <Controller
            name="pedagogy_style"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map((style) => (
                  <button
                    key={style.value}
                    type="button"
                    aria-pressed={field.value === style.value}
                    onClick={() => field.onChange(style.value)}
                    className={[
                      'flex min-h-[44px] items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary',
                      field.value === style.value
                        ? 'border-mindeia-primary bg-mindeia-primary-light text-mindeia-primary'
                        : 'border-surface-200 bg-white text-neutral-700 hover:border-mindeia-primary',
                    ].join(' ')}
                  >
                    <span aria-hidden="true">{style.icon}</span>
                    {t(`teaching.styles.${style.value}`)}
                  </button>
                ))}
              </div>
            )}
          />
          {errors.pedagogy_style && (
            <p role="alert" className="text-xs text-mindeia-error">{t(errors.pedagogy_style.message ?? '')}</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-neutral-800">
            {t('personas.form.description')}{' '}
            <span className="font-normal text-neutral-600">{t('personas.form.optional')}</span>
          </label>
          <textarea
            {...register('description')}
            rows={3}
            maxLength={500}
            placeholder={t('personas.form.description_placeholder')}
            className="rounded-md border border-surface-200 px-3 py-2 text-sm text-neutral-900 placeholder:text-surface-200 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary resize-none"
          />
          {errors.description && (
            <p role="alert" className="text-xs text-mindeia-error">{t(errors.description.message ?? '')}</p>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={isPending} className="flex-1">
            {persona ? t('personas.form.submit_edit') : t('personas.form.submit_create')}
          </Button>
        </div>
      </form>

      {/* Live preview — desktop only */}
      <aside className="hidden lg:flex w-52 flex-col gap-2 flex-shrink-0" aria-label={t('personas.form.preview_label')}>
        <p className="text-xs font-medium text-neutral-600">{t('personas.form.preview_label')}</p>
        <div className="rounded-xl border-2 border-mindeia-primary-light bg-mindeia-primary-subtle p-4 flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mindeia-primary text-white font-bold">
            {watchedName?.[0]?.toUpperCase() ?? '?'}
          </div>
          <p className="font-semibold text-sm text-neutral-900 truncate">
            {watchedName || t('personas.form.preview_name_fallback')}
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedTone && (
              <span className="rounded-full bg-mindeia-primary px-2 py-0.5 text-xs text-white">
                {t(`teaching.tones.${selectedTone}`)}
              </span>
            )}
            {selectedStyle && (
              <span className="rounded-full bg-mindeia-accent px-2 py-0.5 text-xs text-white">
                {t(`teaching.styles.${selectedStyle}`)}
              </span>
            )}
          </div>
        </div>
      </aside>
    </div>
  )
}
